/**
 * LE CLASSEMENT — lecture seule, côté client (§15).
 *
 * ══ Ce que ce module lit, et ce qu'il ne peut PAS écrire ═════════════════════
 *
 * `total_production` est calculée par l'Edge Function `refresh-ranking` et écrite par
 * `service_role`. Le client n'a même pas le privilège de la modifier : c'est une frontière
 * posée à l'étape 2, pour qu'un joueur ne forge pas son rang depuis la console.
 *
 * Ce module ne fait donc que LIRE, et il ne lit que des colonnes publiques : nom, slug,
 * niveau de prestige, production. Jamais `owner_id`, dont la lecture est refusée par la
 * base elle-même (fermée en 0.54.1) — et c'est très bien ainsi : un classement n'a aucun
 * besoin de savoir QUI joue, seulement quelle ville produit combien.
 *
 * ══ Le rang du joueur ne se cherche pas dans la liste ════════════════════════
 *
 * ⚠️ Un top 50 dans lequel le joueur n'apparaît pas ne lui apprend rien — pire, il lui
 * apprend qu'il ne compte pas. Le rang propre se demande donc séparément, et par un
 * COMPTAGE : « combien de villes produisent plus que la mienne ? ». Une seule requête
 * `HEAD`, exacte quel que soit le nombre de joueurs, et sans rapatrier une seule ligne.
 *
 * C'est aussi la seule façon juste : chercher sa ville dans une liste tronquée à cinquante
 * donnerait « non classé » à tout le monde sauf aux cinquante premiers.
 */

import { isDefaultCityName } from '@/core/cityNaming';
import { defaultCityNames } from '@/i18n';
import { getSupabase, isSupabaseConfigured } from './supabase';

/** Une ligne du classement. Rien d'identifiant : ni compte, ni e-mail, ni `owner_id`. */
export interface RankedCity {
  readonly slug: string;
  readonly name: string;
  readonly prestigeLevel: number;
  readonly production: number;
}

export interface Leaderboard {
  readonly top: readonly RankedCity[];
  /**
   * Rang du joueur, 1 pour la première place. `null` si sa ville est inconnue, ou si elle
   * ne produit encore rien — un joueur qui n'a rien posé n'a pas de rang, et lui en
   * inventer un serait moins honnête que de ne rien afficher.
   */
  readonly ownRank: number | null;
  /** Nombre total de villes classées, c'est-à-dire produisant au moins une pièce. */
  readonly ranked: number;
  /**
   * Part des villes classées que le joueur DEVANCE, de 0 à 1.
   *
   * ══ Pourquoi un pourcentage à côté d'un rang ════════════════════════════
   *
   * Un rang seul se lit mal tant que la population est petite ou inconnue : « 12e » est
   * excellent sur quinze joueurs et médiocre sur dix mille. Le pourcentage porte
   * l'information que le rang cache — **où l'on se situe**, pas seulement combien de gens
   * sont devant.
   *
   * Il vaut aussi quand le classement est minuscule, ce qui est le cas aujourd'hui :
   * « devant 50 % des villes » reste vrai et lisible à deux joueurs, là où « 2e » ne dit
   * rien du tout.
   *
   * `null` si le joueur n'est pas classé — on ne compare pas quelqu'un qui n'est pas dans
   * la liste.
   */
  readonly ownPercentile: number | null;
  /**
   * `true` quand la ville du joueur porte encore son nom par défaut.
   *
   * Elle n'est donc pas classée — non par défaut technique mais par règle : un classement
   * de villes toutes appelées « Ma ville » n'apprend rien à personne. L'écran doit le DIRE
   * et proposer le geste, pas afficher un rang absent comme s'il s'agissait d'une panne.
   */
  readonly ownUnnamed: boolean;
  /**
   * `true` quand la table entière est à zéro : la passe de calcul n'a jamais tourné.
   *
   * ⚠️ Cet état existe pour ne pas le confondre avec « personne ne joue ». Sans lui,
   * l'écran afficherait un classement vide, exactement comme si le jeu n'avait aucun
   * joueur — un échec qui ressemble à une donnée, encore. C'est le seul cas où le jeu
   * a quelque chose à dire au lieu d'une liste.
   */
  readonly notComputed: boolean;
}

const TOP_SIZE = 50;

/**
 * Lit le classement, et la place du joueur dedans.
 *
 * Rend `null` — et non un classement vide — si Supabase n'est pas configuré ou si la
 * lecture échoue. La distinction compte : un tableau vide est une réponse (« personne
 * n'est classé »), `null` est une absence de réponse.
 */
export async function fetchLeaderboard(ownSlug: string | null): Promise<Leaderboard | null> {
  /*
    `isSupabaseConfigured()` est la SEULE garde utile : `getSupabase()` ne rend jamais
    `null`, il construit le client à la demande. Le tester en plus donnait une branche
    morte — que le compilateur a refusée, à raison : un contrôle qui ne peut pas être
    faux est un contrôle qui ment sur ce qu'il protège.
  */
  if (!isSupabaseConfigured()) return null;
  const supabase = getSupabase();

  /*
    ══ SEULES LES VILLES NOMMÉES ENTRENT AU CLASSEMENT ═══════════════════════

    « J'ai l'impression qu'il y a beaucoup de doublons » (Benoit, 22 août). Il y en avait :
    894 villes classées, presque toutes appelées « Ma ville ». Un classement dont les
    entrées n'ont pas de nom n'est pas un classement, c'est une liste de nombres.

    Le filtre vit ICI, dans la LECTURE, et surtout **pas dans l'écriture de
    `total_production`.** La tentation était d'arrêter d'écrire la colonne pour les villes
    sans nom — le classement les aurait écartées tout seul. Ç'aurait effacé des villes de
    vrais joueurs : la purge (ROADMAP 0.4) tient une production nulle pour « jamais jouée »
    et **supprime**.

    La liste des noms exclus couvre TOUTES les langues, dérivée des dictionnaires : une
    ville créée en anglais s'appelle « My city », et les deux existent déjà en base.
  */
  const excluded = defaultCityNames();
  /*
    PostgREST attend `not.in.("a","b")`. Les guillemets sont nécessaires — « Ma ville »
    contient une espace — et une virgule dans un nom par défaut casserait la liste. Aucun
    n'en contient aujourd'hui ; si un jour c'est le cas, ce filtre devra passer par une
    fonction serveur plutôt que par une chaîne.
  */
  const notDefault = `("${excluded.join('","')}")`;

  const { data, error } = await supabase
    .from('cities')
    .select('slug, name, prestige_level, total_production')
    .not('name', 'in', notDefault)
    /*
      `gt.0` écarte les milliers de villes de sonde d'un seul prédicat, côté SERVEUR.
      Filtrer après coup aurait ramené mille lignes vides pour en garder cinquante — et se
      serait fait tronquer par la limite de PostgREST avant même d'arriver.
    */
    .gt('total_production', 0)
    .order('total_production', { ascending: false })
    .limit(TOP_SIZE);

  if (error) return null;

  const top: RankedCity[] = data.map((row) => ({
    slug: row.slug,
    name: row.name,
    prestigeLevel: row.prestige_level,
    production: row.total_production,
  }));

  // Combien de villes sont classées, tous joueurs confondus.
  /*
    ⚠️ LE MÊME filtre que la liste. Compter toutes les villes productives donnerait « tu es
    12e sur 894 » à un joueur qui n'en voit que quarante — un rang juste dans un total faux
    est plus déroutant qu'une absence de rang.
  */
  const { count: rankedCount } = await supabase
    .from('cities')
    .select('slug', { count: 'exact', head: true })
    .not('name', 'in', notDefault)
    .gt('total_production', 0);
  const ranked = rankedCount ?? 0;

  if (ranked === 0) {
    return {
      top: [],
      ownRank: null,
      ownPercentile: null,
      ranked: 0,
      ownUnnamed: false,
      notComputed: true,
    };
  }

  let ownRank: number | null = null;
  let ownUnnamed = false;
  if (ownSlug !== null) {
    const { data: own } = await supabase
      .from('cities')
      .select('name, total_production')
      .eq('slug', ownSlug)
      .maybeSingle();

    // Le nom se juge sur la MÊME liste que le filtre de la liste : sinon l'écran dirait
    // « tu n'es pas classé » à un joueur qui figure dans le top, ou l'inverse.
    ownUnnamed = own !== null && isDefaultCityName(own.name, excluded);

    const mine = own?.total_production ?? 0;
    if (mine > 0 && !ownUnnamed) {
      /*
        Le rang est un COMPTAGE de ce qui est strictement au-dessus, plus un. Chercher sa
        ville dans le top 50 aurait rendu « non classé » à tout le monde sauf aux cinquante
        premiers ; ce comptage reste exact au dix-millième joueur.
      */
      const { count } = await supabase
        .from('cities')
        .select('slug', { count: 'exact', head: true })
        .not('name', 'in', notDefault)
        .gt('total_production', mine);
      ownRank = (count ?? 0) + 1;
    }
  }

  /*
    Part devancée : sur `ranked` villes, `ownRank - 1` sont devant. Le premier devance donc
    tout le monde sauf lui-même — d'où le `ranked - 1` au dénominateur, et non `ranked`.
    Avec `ranked === 1`, il n'y a personne à devancer : le pourcentage n'a pas de sens et
    vaut `null` plutôt que 0, qui se lirait « dernier ».
  */
  const ownPercentile = ownRank === null || ranked <= 1 ? null : (ranked - ownRank) / (ranked - 1);

  return { top, ownRank, ownPercentile, ranked, ownUnnamed, notComputed: false };
}
